import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { SelectOption } from '../components/settings/Options'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { currencyFloat } from '../utils/numbers'
import { BaseRecord } from './BaseRecord'
import { Feature } from './Feature'
import { Industry } from './Industry'
import { Payment } from './Payment'
import { RecordDataMap } from './RecordDataMap'
import { FKType } from './SQLBuilder'
import { Subaccount } from './Subaccount'
import { User } from './User'

type DashboardConstructorArgs = Omit<ConstructorArgs<() => Dashboard>, 'date_type'> & {
    date_type: string | null
}

export enum DASHBOARD_TYPE {
    DASHBOARD_TYPE_INCOME, // income YTD, income MTD
    DASHBOARD_TYPE_ROLLUP_SALES, // rollup income YTD, rollup income MTD
    DASHBOARD_TYPE_INVOICES, // invoices
    DASHBOARD_TYPE_ORDERS, // product orders
    DASHBOARD_TYPE_ROYALTIES, // royalties
    DASHBOARD_TYPE_GOOGLE_LEADS, // google leads dashboard
    DASHBOARD_TYPE_REVIEWS,
    DASHBOARD_TYPE_BOOKINGS, // upcoming bookings
    DASHBOARD_TYPE_CLASSES, // upcoming classes
    DASHBOARD_TYPE_RESERVATIONS, // upcoming reservations
    DASHBOARD_TYPE_CLASS_BOOKINGS, // upcoming class bookings
    DASHBOARD_TYPE_PENDING, // jobs to confirm, client requests
    DASHBOARD_TYPE_REBOOKS, // jobs to rebook (next 30d), future rebooks (1m-12m)
    DASHBOARD_TYPE_COMPLETE, // jobs to complete
    DASHBOARD_TYPE_CHECKINS, // checkin history
    DASHBOARD_TYPE_LEADS, // leads (partner edition only for now)
    DASHBOARD_TYPE_CLIENTS, // active leads, churned clients
    DASHBOARD_TYPE_PACKAGES, // active packages, expiring packages
    DASHBOARD_TYPE_SUBSCRIPTIONS, // active subscriptions, expiring subscriptions
    DASHBOARD_TYPE_RECORDS, // recent submissions, clients with forms to fill out
    DASHBOARD_TYPE_CONTRACTS, // recent signatures, clients with contracts to sign
    DASHBOARD_TYPE_EXPENSES, // recent payments made
    DASHBOARD_TYPE_TASKS, // open tasks, recent completions
    DASHBOARD_TYPE_GIFTS, // active gift certificates, expired gcs
    DASHBOARD_TYPE_MESSAGES, // scheduled messages, recent completions
    DASHBOARD_TYPE_EMPLOYEES, // all employees
    DASHBOARD_TYPE_CANCELLATIONS, // recent cancellations
    DASHBOARD_TYPE_ESTIMATES, // active estimates
    DASHBOARD_TYPE_PAYOUTS, // pending payouts
    DASHBOARD_TYPE_DEPOSITS, // recent deposits
    DASHBOARD_TYPE_ALERTS, // special (flash) alert
    DASHBOARD_TYPE_ITEMS, // item purchase history
    DASHBOARD_TYPE_INVENTORY, // product inventory
    DASHBOARD_TYPE_SETUP_LAUNCH, // launch business
    DASHBOARD_TYPE_QUICKSTART, // account setup (deprecated)
    DASHBOARD_TYPE_VERIFICATIONS, // payment verification & setup
    DASHBOARD_TYPE_MY_LOCATION, // franchise dashboard (pro app only)
    DASHBOARD_TYPE_TCR, // tcr setup dashboard (pro only)
    DASHBOARD_TYPE_UPGRADE_TFN, // tfn upgrade dashboard (pro only)
    DASHBOARD_TYPE_MY_PRO, // pro dashboard (client app only)
    DASHBOARD_TYPE_ADD_PRO, // add a pro dashboard (client app only)
    DASHBOARD_TYPE_UPGRADE_PRO, // upgrade to pro account (client app only)
    DASHBOARD_TYPE_DOCUMENTS, // documents
    DASHBOARD_TYPE_TRIGGERS, // smart campaigns
    DASHBOARD_TYPE_BILLINGS, // scheduled invoices
    DASHBOARD_TYPE_LINKS, // online business widget
    DASHBOARD_TYPE_CHARGE, // charge card
    DASHBOARD_TYPE_INVOICE, // send invoice
    DASHBOARD_TYPE_SCHEDULE, // schedule client
    DASHBOARD_TYPE_BOOK, // book pro
    DASHBOARD_TYPE_PAY, // make payment
    DASHBOARD_TYPE_TEXT, // send message
    DASHBOARD_TYPE_INBOX, // inbox (used for welcome screen)
    DASHBOARD_TYPE_CONTACTS, // contacts (used for welcome screen)
    DASHBOARD_TYPE_CALENDAR, // calendar (used for welcome screen)
    DASHBOARD_TYPE_LEADERBOARD, // show industry benchmark & leaderboard
    DASHBOARD_TYPE_ALERTS_SHORTCUTS, // show key app shortcut(s)
    DASHBOARD_TYPE_ALERTS_CREDENTIAL, // broken credential repair
    DASHBOARD_TYPE_ALERTS_UPDATE_READER, // required update available for card reader
    DASHBOARD_TYPE_RELEASE, // New feature!
    DASHBOARD_TYPE_POSTS, // posts (create and publish posts)
    DASHBOARD_TYPE_FEEDS, // posts (consume posts from multiple sources)
    DASHBOARD_TYPE_SUBSCRIBERS, // posts (view and manage subscribers)
    DASHBOARD_TYPE_LOCATIONS, // locations (view and manage locations)
    DASHBOARD_TYPE_SETUP_ONBOARDING, // onboarding 4.0 setup
    DASHBOARD_TYPE_ALERTS_TEAM, // Upgrade -> Teams alert
    DASHBOARD_TYPE_ALERTS_ASAP, // Request call ASAP alert
    DASHBOARD_TYPE_ALERTS_RENEW, // Renew subscription alert
    DASHBOARD_TYPE_ALERTS_REFER, // Refer a pro alert alert
    DASHBOARD_TYPE_ALERTS_REVIEW, // Leave us a review alert
    DASHBOARD_TYPE_ALERTS_READER, // Order a card reader alert
    DASHBOARD_TYPE_ALERTS_AFFILIATE, // Upgrade annual, save $50 alert
    DASHBOARD_TYPE_ALERTS_TAX_FORM, // Request your 1099-K today
    DASHBOARD_TYPE_ALERTS_NPS,
}

export enum SETUP_TYPE {
    SETUP_TYPE_PROFILE, // setup profile
    SETUP_TYPE_BUSINESS, // setup business
    SETUP_TYPE_CALENDAR_SYNC, // setup calendar sync
    SETUP_TYPE_GRATUITY, // setup gratuity
    SETUP_TYPE_DISCOUNT, // setup discount
    SETUP_TYPE_DESCRIPTIONS, // field defaults
    SETUP_TYPE_NOTIFICATIONS, // notifications
    SETUP_TYPE_CLIENT_NOTIFICATIONS, // client notifications
    SETUP_TYPE_ITEMS, // setup prices
    SETUP_TYPE_CLASSES, // setup classes
    SETUP_TYPE_PACKAGES, // setup packages
    SETUP_TYPE_PRODUCTS, // setup products
    SETUP_TYPE_GIFTS, // setup gift certificates
    SETUP_TYPE_SUBSCRIPTIONS, // setup subscription plans
    SETUP_TYPE_RESERVATIONS, // setup reservations
    SETUP_TYPE_CATEGORIES, // categories
    SETUP_TYPE_SORT_PRICES, // sort prices
    SETUP_TYPE_CLIENTS, // setup address book sync
    SETUP_TYPE_WATCH_OVERVIEW, // learn more (watch overview video)
    SETUP_TYPE_TUTORIALS, // learn more (tutorials screen)
    SETUP_TYPE_CONTACT_US, // contact PocketSuite
    SETUP_TYPE_REQUEST_CALL, // Request ASAP call
    SETUP_TYPE_LEARN_MORE, // view SuiteCenter (FAQ)
    SETUP_TYPE_LEARN_SETUP, // view SuiteCenter setup guide
    SETUP_TYPE_LEARN_STAFFING, // view SuiteCenter staffing guide
    SETUP_TYPE_LEARN_PAYMENTS, // view SuiteCenter payments guide
    SETUP_TYPE_PAYMENTS, // setup payment methods
    SETUP_TYPE_ROYALTY_FEES, // setup royalty fees
    SETUP_TYPE_ROYALTIES, // setup royalty payments
    SETUP_TYPE_PAYOUTS, // setup payout schedules
    SETUP_TYPE_SALESTAX, // setup sales tax rates
    SETUP_TYPE_EMPLOYEES, // add employees
    SETUP_TYPE_FIELDS, // setup fields
    SETUP_TYPE_RECORDS, // setup forms
    SETUP_TYPE_CONTRACTS, // setup contracts
    SETUP_TYPE_TEMPLATES, // setup templates
    SETUP_TYPE_BILLING, // setup scheduled invoices (billing)
    SETUP_TYPE_ESTIMATE, // setup estimates
    SETUP_TYPE_REMINDER, // setup reminders
    SETUP_TYPE_GROUP, // setup group messages
    SETUP_TYPE_SERVICES, // setup multiple services
    SETUP_TYPE_RECURRING, // setup recurring appts
    SETUP_TYPE_WIDGETS, // setup fields & widgets
    SETUP_TYPE_LOCATION, // setup address
    SETUP_TYPE_SIGN_OUT, // sign out
    SETUP_TYPE_AVAILABILITY, // availability
    SETUP_TYPE_CANCELLATIONS, // cancellation policy
    SETUP_TYPE_VERIFY_ACCOUNT, // submit DL/SSN/DOB for verification
    SETUP_TYPE_VERIFY_BUSINESS, // submit business questionnaire for verification
    SETUP_TYPE_ENABLE_FEATURES, // enable features
    SETUP_TYPE_FREE_TRIAL, // start free trial
    SETUP_TYPE_VERIFY_CHECKING, // add a checking (if payment)
    SETUP_TYPE_VERIFY_DEBIT, // add a debit (or credit) card (if payment)
    SETUP_TYPE_VERIFY_LEGAL_ADDRESS, // fix Stripe verification error: full name
    SETUP_TYPE_VERIFY_LEGAL_NAME, // fix Stripe verification error: address,
    SETUP_TYPE_VERIFY_ID_NUMBER, // fix Stripe verification error: social security number,
    SETUP_TYPE_ORDERING, // setup online ordering (currently part of the booking widget)
    SETUP_TYPE_ONLINE_BOOKING, // setup online booking
    SETUP_TYPE_ONLINE_PAYMENT, // setup payment widget
    SETUP_TYPE_ONLINE_ORDERING, // setup online ordering
    SETUP_TYPE_ONLINE_LEAD, // setup lead widget
    SETUP_TYPE_ONLINE_CHAT, // setup chat widget
    SETUP_TYPE_SUITELINK, // setup Suite link
    SETUP_TYPE_GOOGLE_LEADS, // Reserve & Leads with Google
    SETUP_TYPE_REQUIRE_CARD, // setup require CC status for bookings
    SETUP_TYPE_BOOKING,
    SETUP_TYPE_INVOICE,
    SETUP_TYPE_MESSAGE,
    SETUP_TYPE_CHARGE,
    SETUP_TYPE_PAYMENT,
    SETUP_TYPE_PACKAGE,
    SETUP_TYPE_CONTRACT,
    SETUP_TYPE_SUBSCRIPTION,
    SETUP_TYPE_VERIFY_NUMBER,
    SETUP_TYPE_FEATURE_WIDGET,
    SETUP_TYPE_FEATURE_STAFFING,
    SETUP_TYPE_UPGRADE,
    SETUP_TYPE_PREMIUM_NUMBER,
    SETUP_TYPE_READERS,
    SETUP_TYPE_LEGAL,
    SETUP_TYPE_REFER,
    SETUP_TYPE_REFER_CLIENT,
    SETUP_TYPE_DATA_EXPORT,
    SETUP_TYPE_DATA_IMPORT,
    SETUP_TYPE_TAX_FORMS,
    SETUP_TYPE_LOGIN_SECURITY,
    SETUP_TYPE_VIDEO_CONFERENCING,
    SETUP_TYPE_RESOURCES,
    SETUP_TYPE_RESOURCE_GROUPS,
}

export enum METRIC_DATE_TYPE {
    METRIC_DATE_WEEK,
    METRIC_DATE_THIS_WEEK,
    METRIC_DATE_LAST_WEEK,
    METRIC_DATE_NEXT_WEEK,
    METRIC_DATE_WEEK_TO_DATE,
    METRIC_DATE_MONTH,
    METRIC_DATE_THIS_MONTH,
    METRIC_DATE_LAST_MONTH,
    METRIC_DATE_NEXT_MONTH,
    METRIC_DATE_MONTH_TO_DATE,
    METRIC_DATE_QUARTER,
    METRIC_DATE_THIS_QUARTER,
    METRIC_DATE_LAST_QUARTER,
    METRIC_DATE_NEXT_QUARTER,
    METRIC_DATE_QUARTER_TO_DATE,
    METRIC_DATE_YEAR,
    METRIC_DATE_THIS_YEAR,
    METRIC_DATE_LAST_YEAR,
    METRIC_DATE_LAST_YEAR_BY_QUARTER,
    METRIC_DATE_NEXT_YEAR,
    METRIC_DATE_YEAR_TO_DATE,
    METRIC_DATE_ALL,
}

export enum METRIC_TYPE {
    METRIC_TYPE_RECEIVABLES,
    METRIC_TYPE_INCOME,
    METRIC_TYPE_SALESTAX,
    METRIC_TYPE_REFUNDS,
    METRIC_TYPE_REFERRALS,
}

export enum DASHBOARD_TAB {
    DASHBOARD_TAB_NONE,
    DASHBOARD_TAB_MAIN,
    DASHBOARD_TAB_ACCOUNT,
}

export class Dashboard extends BaseRecord {
    readonly recordType = 'dashboard'

    view?: number // Integer
    alwaysDrilldown?: boolean
    title?: string
    metric?: string
    metricType?: string
    count?: number // Integer
    value?: number
    lastModifiedDate?: moment.Moment
    isForSetup?: boolean
    results: any[] = []
    _results: any[] = []
    readonly viewCount: number = 0
    readonly metaData: RecordDataMap = new RecordDataMap()
    readonly dateType?: METRIC_DATE_TYPE
    readonly dateTypeAsString?: string
    private readonly _status?: string
    private readonly _longStatus?: string

    constructor(data?: Partial<DashboardConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.view = this.propertyFromIntArg(data.view)
        this.alwaysDrilldown = data.always_drilldown ?? false
        this.title = data.title ?? undefined
        this.metric = data.metric ?? undefined
        this.metricType = data.metric_type ?? undefined
        this.count = this.propertyFromIntArg(data.count)
        this.value = this.propertyFromCurrencyArg(data.value)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.isForSetup = data.is_for_setup ?? false
        this.viewCount = this.propertyFromIntArg(data.view_count) ?? 0
        this.metaData = this.propertyFromDictionaryArg(data.meta_data) ?? new RecordDataMap()
        this.dateTypeAsString = data.date_type ?? undefined
        this.dateType = (() => {
            if (this.hasDateSegments) {
                if (!!data.date_type) {
                    switch (this.dateTypeAsString) {
                        case 'wtd':
                            return METRIC_DATE_TYPE.METRIC_DATE_WEEK_TO_DATE
                        case 'mtd':
                            return METRIC_DATE_TYPE.METRIC_DATE_MONTH_TO_DATE
                        case 'ytd':
                            return METRIC_DATE_TYPE.METRIC_DATE_YEAR_TO_DATE
                        case 'week':
                            return METRIC_DATE_TYPE.METRIC_DATE_WEEK
                        case 'qtd':
                            return METRIC_DATE_TYPE.METRIC_DATE_QUARTER_TO_DATE
                        case 'quarter':
                            return METRIC_DATE_TYPE.METRIC_DATE_QUARTER
                        case 'month':
                            return METRIC_DATE_TYPE.METRIC_DATE_MONTH
                        case 'year':
                            return METRIC_DATE_TYPE.METRIC_DATE_YEAR
                        case 'tq':
                            return METRIC_DATE_TYPE.METRIC_DATE_THIS_QUARTER
                        case 'tw':
                            return METRIC_DATE_TYPE.METRIC_DATE_THIS_WEEK
                        case 'tm':
                            return METRIC_DATE_TYPE.METRIC_DATE_THIS_MONTH
                        case 'ty':
                            return METRIC_DATE_TYPE.METRIC_DATE_THIS_YEAR
                        default:
                            return METRIC_DATE_TYPE.METRIC_DATE_ALL
                    }
                }
                return this.person !== undefined
                    ? METRIC_DATE_TYPE.METRIC_DATE_YEAR_TO_DATE
                    : METRIC_DATE_TYPE.METRIC_DATE_MONTH_TO_DATE
            }
            return undefined
        })()
        this._status = data.status ?? undefined
        this._longStatus = data.long_status ?? undefined
        this._results = data.results ?? []
    }

    clone(overrides?: Partial<DashboardConstructorArgs>): Dashboard {
        const newProps = { ...this.serializeForClone(), ...overrides }
        newProps.date_type = (() => {
            if (!newProps.date_type) {
                return undefined
            }

            // date_type needs to come in as a string, but the property on
            // this class is then converted into a METRIC_DATE_TYPE. So here
            // we map it back to a string if necessary.
            switch (newProps.date_type as string | METRIC_DATE_TYPE) {
                case METRIC_DATE_TYPE.METRIC_DATE_WEEK_TO_DATE:
                    return 'wtd'
                case METRIC_DATE_TYPE.METRIC_DATE_MONTH_TO_DATE:
                    return 'mtd'
                case METRIC_DATE_TYPE.METRIC_DATE_YEAR_TO_DATE:
                    return 'ytd'
                case METRIC_DATE_TYPE.METRIC_DATE_WEEK:
                    return 'week'
                case METRIC_DATE_TYPE.METRIC_DATE_QUARTER_TO_DATE:
                    return 'qtd'
                case METRIC_DATE_TYPE.METRIC_DATE_QUARTER:
                    return 'quarter'
                case METRIC_DATE_TYPE.METRIC_DATE_MONTH:
                    return 'month'
                case METRIC_DATE_TYPE.METRIC_DATE_YEAR:
                    return 'year'
                case METRIC_DATE_TYPE.METRIC_DATE_THIS_QUARTER:
                    return 'tq'
                case METRIC_DATE_TYPE.METRIC_DATE_THIS_WEEK:
                    return 'tw'
                case METRIC_DATE_TYPE.METRIC_DATE_THIS_MONTH:
                    return 'tm'
                case METRIC_DATE_TYPE.METRIC_DATE_THIS_YEAR:
                    return 'ty'
                default:
                    return ''
            }
        })()

        return new Dashboard(newProps)
    }

    get me(): User {
        return PSApplication.me()
    }

    get owner(): User {
        return this.me.owner
    }

    get forVendor(): boolean {
        return this.me.sp
    }

    get industry(): Industry | undefined {
        return this.me?.industry
    }

    /**
     * Should user be able to drill down to a detail version of this dashboard
     * return true if there is a detail drilldown for this dashboard (usually true)
     */
    get showDrilldown(): boolean | undefined {
        if (
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_SHORTCUTS ||
            this.forAlert ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_VERIFICATIONS ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_CHARGE ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICE ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_SCHEDULE ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_BOOK ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_PAY ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_TEXT ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_CREDENTIAL ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_RELEASE ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_UPDATE_READER ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_TCR ||
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_UPGRADE_TFN
        )
            return false
        if (
            this.me?.payee &&
            (this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_BOOKINGS ||
                this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_PENDING ||
                this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_COMPLETE)
        )
            return true
        return (
            this.type == DASHBOARD_TYPE.DASHBOARD_TYPE_LEADERBOARD ||
            (this.count ?? 0) > 0 ||
            this.alwaysDrilldown
        )
    }

    // todo: replace with JSX.Element, check latest model for updates here when implementing todo
    // get image(): number | undefined {
    //     if (this.id === "income")
    //         return R.drawable.dashboard_income
    //     else if (this.id === "salestax")
    //         return R.drawable.dashboard_salestax
    //     else if (this.id === "invoices")
    //         return R.drawable.dashboard_invoices
    //     else if (this.id === "billings")
    //         return R.drawable.dashboard_billing
    //     else if (this.id === "estimates")
    //         return R.drawable.dashboard_estimates
    //     else if (this.id === "tasks")
    //         return R.drawable.dashboard_tasks
    //     else if (this.id === "documents")
    //         return R.drawable.settings_memos
    //     else if (this.id === "messages")
    //         return R.drawable.dashboard_messages
    //     else if (this.id === "bookings")
    //         return R.drawable.dashboard_schedule
    //     else if (this.id === "classes" || this.id === "class_bookings")
    //         return R.drawable.dashboard_classes
    //     else if (this.id === "rebooks")
    //         return R.drawable.dashboard_rebook
    //     else if (this.id === "pending")
    //         return R.drawable.dashboard_confirm
    //     else if (this.id === "complete")
    //         return R.drawable.dashboard_complete
    //     else if (this.id === "clients")
    //         return R.drawable.dashboard_clients
    //     else if (this.id === "packages")
    //         return R.drawable.dashboard_packages
    //     else if (this.id === "orders")
    //         return R.drawable.dashboard_orders
    //     else if (this.id === "subscriptions")
    //         return R.drawable.dashboard_subscriptions
    //     else if (this.id === "records")
    //         return R.drawable.dashboard_forms
    //     else if (this.id === "contracts")
    //         return R.drawable.dashboard_contracts
    //     else if (this.id === "expenses")
    //         return R.drawable.dashboard_expenses
    //     else if (this.id === "employees")
    //         return R.drawable.dashboard_staffing
    //     else if (this.id === "cancellations")
    //         return R.drawable.dashboard_cancellations
    //     else if (this.id === "payouts")
    //         return R.drawable.dashboard_payouts
    //     else if (this.id === "deposits")
    //         return R.drawable.dashboard_deposits
    //     else if (this.id === "setup")
    //         return R.drawable.dashboard_setup
    //     else if (this.id === "verifications")
    //         return R.drawable.dashboard_verifications
    //     else if (this.id === "google_leads")
    //         return R.drawable.ps_general_leads
    //     else if (this.id === "reviews")
    //         return R.drawable.ps_general_review
    //     else if (this.id === "gifts")
    //         return R.drawable.dashboard_gifts
    //     else if (this.id === "items")
    //         return R.drawable.dashboard_items
    //     else if (this.id === "inventory")
    //         return R.drawable.dashboard_products
    //     else if (this.id === "triggers")
    //         return R.drawable.dashboard_triggers
    //     else if (this.id === "leaderboard")
    //         return R.drawable.dashboard_leaderboard
    //     else if (this.id === "links")
    //         return R.drawable.dashboard_widget
    //     else if (this.id === "charge")
    //         return R.drawable.dashboard_charge
    //     else if (this.id === "schedule")
    //         return R.drawable.dashboard_schedule
    //     else if (this.id === "invoice")
    //         return R.drawable.dashboard_invoice
    //     else if (this.id === "book")
    //         return R.drawable.dashboard_book
    //     else if (this.id === "pay")
    //         return R.drawable.dashboard_pay
    //     else if (this.id === "text")
    //         return R.drawable.dashboard_text
    //     else if (this.id === "add_pro")
    //         return R.drawable.dashboard_add_pro
    //     else if (this.id === "alerts_credential")
    //         return R.drawable.dashboard_credential
    //     else if (this.id === "alerts_update_reader")
    //         return R.drawable.red_exclamatory_mark
    //     return -1
    // }

    get type(): DASHBOARD_TYPE | undefined {
        if (this.id === 'income') return DASHBOARD_TYPE.DASHBOARD_TYPE_INCOME
        else if (this.id === 'rollup_sales') return DASHBOARD_TYPE.DASHBOARD_TYPE_ROLLUP_SALES
        else if (this.id === 'invoices') return DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICES
        else if (this.id === 'billings') return DASHBOARD_TYPE.DASHBOARD_TYPE_BILLINGS
        else if (this.id === 'estimates') return DASHBOARD_TYPE.DASHBOARD_TYPE_ESTIMATES
        else if (this.id === 'tasks') return DASHBOARD_TYPE.DASHBOARD_TYPE_TASKS
        else if (this.id === 'documents') return DASHBOARD_TYPE.DASHBOARD_TYPE_DOCUMENTS
        else if (this.id === 'messages') return DASHBOARD_TYPE.DASHBOARD_TYPE_MESSAGES
        else if (this.id === 'bookings') return DASHBOARD_TYPE.DASHBOARD_TYPE_BOOKINGS
        else if (this.id === 'orders') return DASHBOARD_TYPE.DASHBOARD_TYPE_ORDERS
        else if (this.id === 'royalties') return DASHBOARD_TYPE.DASHBOARD_TYPE_ROYALTIES
        else if (this.id === 'google_leads') return DASHBOARD_TYPE.DASHBOARD_TYPE_GOOGLE_LEADS
        else if (this.id === 'reviews') return DASHBOARD_TYPE.DASHBOARD_TYPE_REVIEWS
        else if (this.id === 'classes') return DASHBOARD_TYPE.DASHBOARD_TYPE_CLASSES
        else if (this.id === 'reservations') return DASHBOARD_TYPE.DASHBOARD_TYPE_RESERVATIONS
        else if (this.id === 'class_bookings') return DASHBOARD_TYPE.DASHBOARD_TYPE_CLASS_BOOKINGS
        else if (this.id === 'rebooks') return DASHBOARD_TYPE.DASHBOARD_TYPE_REBOOKS
        else if (this.id === 'pending') return DASHBOARD_TYPE.DASHBOARD_TYPE_PENDING
        else if (this.id === 'complete') return DASHBOARD_TYPE.DASHBOARD_TYPE_COMPLETE
        else if (this.id === 'leads') return DASHBOARD_TYPE.DASHBOARD_TYPE_LEADS
        else if (this.id === 'clients') return DASHBOARD_TYPE.DASHBOARD_TYPE_CLIENTS
        else if (this.id === 'packages') return DASHBOARD_TYPE.DASHBOARD_TYPE_PACKAGES
        else if (this.id === 'subscriptions') return DASHBOARD_TYPE.DASHBOARD_TYPE_SUBSCRIPTIONS
        else if (this.id === 'records') return DASHBOARD_TYPE.DASHBOARD_TYPE_RECORDS
        else if (this.id === 'contracts') return DASHBOARD_TYPE.DASHBOARD_TYPE_CONTRACTS
        else if (this.id === 'expenses') return DASHBOARD_TYPE.DASHBOARD_TYPE_EXPENSES
        else if (this.id === 'employees') return DASHBOARD_TYPE.DASHBOARD_TYPE_EMPLOYEES
        else if (this.id === 'cancellations') return DASHBOARD_TYPE.DASHBOARD_TYPE_CANCELLATIONS
        else if (this.id === 'payouts') return DASHBOARD_TYPE.DASHBOARD_TYPE_PAYOUTS
        else if (this.id === 'deposits') return DASHBOARD_TYPE.DASHBOARD_TYPE_DEPOSITS
        else if (this.id === 'alerts_asap') return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_ASAP
        else if (this.id === 'alerts_team') return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_TEAM
        else if (this.id === 'alerts_refer') return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_REFER
        else if (this.id === 'alerts_renew') return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_RENEW
        else if (this.id === 'alerts_review') return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_REVIEW
        else if (this.id === 'alerts_reader') return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_READER
        else if (this.id === 'alerts_affiliate')
            return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_AFFILIATE
        else if (this.id === 'alerts_tax_form') return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_TAX_FORM
        else if (this.id === 'setup') return DASHBOARD_TYPE.DASHBOARD_TYPE_QUICKSTART
        else if (this.id === 'setup_launch') return DASHBOARD_TYPE.DASHBOARD_TYPE_SETUP_LAUNCH
        else if (this.id === 'setup_onboarding')
            return DASHBOARD_TYPE.DASHBOARD_TYPE_SETUP_ONBOARDING
        else if (this.id === 'verifications') return DASHBOARD_TYPE.DASHBOARD_TYPE_VERIFICATIONS
        else if (this.id === 'gifts') return DASHBOARD_TYPE.DASHBOARD_TYPE_GIFTS
        else if (this.id === 'items') return DASHBOARD_TYPE.DASHBOARD_TYPE_ITEMS
        else if (this.id === 'inventory') return DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY
        else if (this.id === 'triggers') return DASHBOARD_TYPE.DASHBOARD_TYPE_TRIGGERS
        else if (this.id === 'my_pro') return DASHBOARD_TYPE.DASHBOARD_TYPE_MY_PRO
        else if (this.id === 'my_location') return DASHBOARD_TYPE.DASHBOARD_TYPE_MY_LOCATION
        else if (this.id === 'add_pro') return DASHBOARD_TYPE.DASHBOARD_TYPE_ADD_PRO
        else if (this.id === 'upgrade_pro') return DASHBOARD_TYPE.DASHBOARD_TYPE_UPGRADE_PRO
        else if (this.id === 'leaderboard') return DASHBOARD_TYPE.DASHBOARD_TYPE_LEADERBOARD
        else if (this.id === 'links') return DASHBOARD_TYPE.DASHBOARD_TYPE_LINKS
        else if (this.id === 'charge') return DASHBOARD_TYPE.DASHBOARD_TYPE_CHARGE
        else if (this.id === 'schedule') return DASHBOARD_TYPE.DASHBOARD_TYPE_SCHEDULE
        else if (this.id === 'invoice') return DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICE
        else if (this.id === 'book') return DASHBOARD_TYPE.DASHBOARD_TYPE_BOOK
        else if (this.id === 'pay') return DASHBOARD_TYPE.DASHBOARD_TYPE_PAY
        else if (this.id === 'text') return DASHBOARD_TYPE.DASHBOARD_TYPE_TEXT
        else if (this.id === 'inbox') return DASHBOARD_TYPE.DASHBOARD_TYPE_INBOX
        else if (this.id === 'contacts') return DASHBOARD_TYPE.DASHBOARD_TYPE_CONTACTS
        else if (this.id === 'calendar') return DASHBOARD_TYPE.DASHBOARD_TYPE_CALENDAR
        else if (this.id === 'alerts_shortcuts')
            return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_SHORTCUTS
        else if (this.id === 'alerts_credential')
            return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_CREDENTIAL
        else if (this.id === 'alerts_update_reader')
            return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_UPDATE_READER
        else if (this.id === 'alerts_nps') return DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_NPS
        else if (this.id === 'posts') return DASHBOARD_TYPE.DASHBOARD_TYPE_POSTS
        else if (this.id === 'feeds') return DASHBOARD_TYPE.DASHBOARD_TYPE_FEEDS
        else if (this.id === 'subscribers') return DASHBOARD_TYPE.DASHBOARD_TYPE_SUBSCRIBERS
        else if (this.id === 'locations') return DASHBOARD_TYPE.DASHBOARD_TYPE_LOCATIONS
        else if (this.id === 'setup_tcr') return DASHBOARD_TYPE.DASHBOARD_TYPE_TCR
        else if (this.id === 'upgrade_tfn') return DASHBOARD_TYPE.DASHBOARD_TYPE_UPGRADE_TFN
        return undefined
    }

    get setupType(): SETUP_TYPE | undefined {
        if (this.id === 'profile') return SETUP_TYPE.SETUP_TYPE_PROFILE
        else if (this.id === 'business') return SETUP_TYPE.SETUP_TYPE_BUSINESS
        else if (this.id === 'calendar_sync') return SETUP_TYPE.SETUP_TYPE_CALENDAR_SYNC
        else if (this.id === 'gratuity') return SETUP_TYPE.SETUP_TYPE_GRATUITY
        else if (this.id === 'discount') return SETUP_TYPE.SETUP_TYPE_DISCOUNT
        else if (this.id === 'descriptions') return SETUP_TYPE.SETUP_TYPE_DESCRIPTIONS
        else if (this.id === 'notifications') return SETUP_TYPE.SETUP_TYPE_NOTIFICATIONS
        else if (this.id === 'client_notifications')
            return SETUP_TYPE.SETUP_TYPE_CLIENT_NOTIFICATIONS
        else if (this.id === 'items') return SETUP_TYPE.SETUP_TYPE_ITEMS
        else if (this.id === 'classes') return SETUP_TYPE.SETUP_TYPE_CLASSES
        else if (this.id === 'packages') return SETUP_TYPE.SETUP_TYPE_PACKAGES
        else if (this.id === 'products') return SETUP_TYPE.SETUP_TYPE_PRODUCTS
        else if (this.id === 'gifts') return SETUP_TYPE.SETUP_TYPE_GIFTS
        else if (this.id === 'subscriptions') return SETUP_TYPE.SETUP_TYPE_SUBSCRIPTIONS
        else if (this.id === 'reservations') return SETUP_TYPE.SETUP_TYPE_RESERVATIONS
        else if (this.id === 'categories') return SETUP_TYPE.SETUP_TYPE_CATEGORIES
        else if (this.id === 'sort_prices') return SETUP_TYPE.SETUP_TYPE_SORT_PRICES
        else if (this.id === 'clients') return SETUP_TYPE.SETUP_TYPE_CLIENTS
        else if (this.id === 'watch_overview') return SETUP_TYPE.SETUP_TYPE_WATCH_OVERVIEW
        else if (this.id === 'tutorials') return SETUP_TYPE.SETUP_TYPE_TUTORIALS
        else if (this.id === 'contact_us') return SETUP_TYPE.SETUP_TYPE_CONTACT_US
        else if (this.id === 'request_call') return SETUP_TYPE.SETUP_TYPE_REQUEST_CALL
        else if (this.id === 'learn_more') return SETUP_TYPE.SETUP_TYPE_LEARN_MORE
        else if (this.id === 'clients') return SETUP_TYPE.SETUP_TYPE_CLIENTS
        else if (this.id === 'items') return SETUP_TYPE.SETUP_TYPE_ITEMS
        else if (this.id === 'cancellations') return SETUP_TYPE.SETUP_TYPE_CANCELLATIONS
        else if (this.id === 'new_charge') return SETUP_TYPE.SETUP_TYPE_CHARGE
        else if (this.id === 'new_invoice') return SETUP_TYPE.SETUP_TYPE_INVOICE
        else if (this.id === 'new_booking') return SETUP_TYPE.SETUP_TYPE_BOOKING
        else if (this.id === 'new_payment') return SETUP_TYPE.SETUP_TYPE_PAYMENT
        else if (this.id === 'new_package') return SETUP_TYPE.SETUP_TYPE_PACKAGE
        else if (this.id === 'new_contract') return SETUP_TYPE.SETUP_TYPE_CONTRACT
        else if (this.id === 'new_subscription') return SETUP_TYPE.SETUP_TYPE_SUBSCRIPTION
        else if (this.id === 'new_number') return SETUP_TYPE.SETUP_TYPE_PREMIUM_NUMBER
        else if (this.id === 'employees') return SETUP_TYPE.SETUP_TYPE_EMPLOYEES
        else if (this.id === 'lead') return SETUP_TYPE.SETUP_TYPE_ONLINE_LEAD
        else if (this.id === 'widget') return SETUP_TYPE.SETUP_TYPE_FEATURE_WIDGET
        else if (this.id === 'staffing') return SETUP_TYPE.SETUP_TYPE_FEATURE_STAFFING
        else if (this.id === 'services') return SETUP_TYPE.SETUP_TYPE_SERVICES
        else if (this.id === 'subscriptions') return SETUP_TYPE.SETUP_TYPE_SUBSCRIPTIONS
        else if (this.id === 'recurring') return SETUP_TYPE.SETUP_TYPE_RECURRING
        else if (this.id === 'class') return SETUP_TYPE.SETUP_TYPE_CLASSES
        else if (this.id === 'package') return SETUP_TYPE.SETUP_TYPE_PACKAGES
        else if (this.id === 'product') return SETUP_TYPE.SETUP_TYPE_PRODUCTS
        else if (this.id === 'reservation') return SETUP_TYPE.SETUP_TYPE_RESERVATIONS
        else if (this.id === 'gratuity') return SETUP_TYPE.SETUP_TYPE_GRATUITY
        else if (this.id === 'record') return SETUP_TYPE.SETUP_TYPE_RECORDS
        else if (this.id === 'contract') return SETUP_TYPE.SETUP_TYPE_CONTRACTS
        else if (this.id === 'template') return SETUP_TYPE.SETUP_TYPE_TEMPLATES
        else if (this.id === 'billing') return SETUP_TYPE.SETUP_TYPE_BILLING
        else if (this.id === 'reminder') return SETUP_TYPE.SETUP_TYPE_REMINDER
        else if (this.id === 'estimate') return SETUP_TYPE.SETUP_TYPE_ESTIMATE
        else if (this.id === 'group') return SETUP_TYPE.SETUP_TYPE_GROUP
        else if (this.id === 'notifications') return SETUP_TYPE.SETUP_TYPE_NOTIFICATIONS
        else if (this.id === 'descriptions') return SETUP_TYPE.SETUP_TYPE_DESCRIPTIONS
        else if (this.id === 'profile') return SETUP_TYPE.SETUP_TYPE_PROFILE
        else if (this.id === 'business') return SETUP_TYPE.SETUP_TYPE_BUSINESS
        else if (this.id === 'availability') return SETUP_TYPE.SETUP_TYPE_AVAILABILITY
        else if (this.id === 'sync') return SETUP_TYPE.SETUP_TYPE_CALENDAR_SYNC
        else if (this.id === 'free_trial') return SETUP_TYPE.SETUP_TYPE_FREE_TRIAL
        else if (this.id === 'contact_us') return SETUP_TYPE.SETUP_TYPE_CONTACT_US
        else if (this.id === 'online_booking') return SETUP_TYPE.SETUP_TYPE_ONLINE_BOOKING
        else if (this.id === 'suitelink') return SETUP_TYPE.SETUP_TYPE_SUITELINK
        else if (this.id === 'require_card') return SETUP_TYPE.SETUP_TYPE_REQUIRE_CARD
        return SETUP_TYPE.SETUP_TYPE_PROFILE
    }

    // static setupIcon(id: string): IconName {
    //     if (id === 'purchase_reader') return R.drawable.setup_icon_purchase_reader
    //     else if (id === 'watch_overview') return R.drawable.setup_icon_watch_overview
    //     else if (id === 'learn_setup') return R.drawable.setup_icon_learn_more
    //     else if (id === 'learn_staffing') return R.drawable.setup_icon_learn_staffing
    //     else if (id === 'learn_payments') return R.drawable.setup_icon_learn_more
    //     else if (id === 'items') return R.drawable.setup_icon_items
    //     else if (id === 'new_contract') return R.drawable.setup_icon_contracts
    //     else if (id === 'new_number') return R.drawable.setup_icon_new_number
    //     else if (id === 'employees') return R.drawable.setup_icon_employees
    //     else if (id === 'notifications') return R.drawable.setup_icon_notifications
    //     else if (id === 'descriptions') return R.drawable.setup_icon_descriptions
    //     else if (id === 'profile') return R.drawable.setup_icon_profile
    //     else if (id === 'availability') return R.drawable.setup_icon_availability
    //     else if (id === 'sync') return R.drawable.setup_icon_sync
    //     else if (id === 'online_booking') return R.drawable.setup_icon_online_booking
    //     else if (id === 'suitelink') return R.drawable.setup_icon_suitelink
    //     else if (id === 'require_card') return R.drawable.setup_icon_require_card
    //     else if (id === 'verify_business') return R.drawable.setup_icon_verify_business
    //     else if (id === 'verify_account') return R.drawable.setup_icon_verify_business
    //     return R.drawable.setup_icon_profile
    // }

    /**
     * Return number of views in dashboard
     * return # of views
     */
    get numberOfViews(): number {
        return this.viewStrings.length
    }

    get viewStrings(): string[] {
        const l: string[] = []
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_EXPENSES) {
            l.push('Payments')
            l.push('Future payments')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CLASS_BOOKINGS) {
            l.push('Scheduled')
            l.push('')
            l.push('Canceled')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_MESSAGES) {
            l.push('Scheduled')
            l.push('Completed')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_REVIEWS) {
            l.push('PocketSuite')
            l.push('Google')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CLASSES) {
            l.push('Scheduled')
            l.push('')
            l.push('Canceled')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CLIENTS) {
            l.push('New')
            l.push('Active')
            l.push('Paying')
            l.push('Past')
            l.push('Deleted')
            l.push('Blocked')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_LEADS) {
            l.push('Not routed')
            l.push('Routed')
            l.push('New')
            l.push('Deleted')
            l.push('Blocked')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICES) {
            l.push('Open')
            l.push('Paid')
            l.push('Sent')
            l.push('Viewed')
            l.push('Canceled')
            if (PSApplication.role() > User.PERM_ROLE_CLIENT) l.push('Saved')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RESERVATIONS) {
            l.push('Active')
            l.push('In-progress')
            l.push('Checking in')
            l.push('Checking out')
            l.push('Checked out')
            if (this.me.allowStaffing) {
                l.push('Unassigned')
            }
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ORDERS) {
            l.push('Open')
            l.push('Pickup')
            l.push('Shipping')
            l.push('Unconfirmed')
            l.push('Unpaid')
            l.push('Paid not completed')
            l.push('Completed')
            l.push('Canceled')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ROYALTIES) {
            l.push('Unpaid')
            l.push('Paid')
            l.push('Canceled')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_BILLINGS) {
            l.push('Scheduled')
            l.push('Canceled')
            l.push('Completed')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_DOCUMENTS) {
            l.push('All')
            l.push('Expiring')
            l.push('Expired')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PACKAGES) {
            l.push('Active')
            l.push('Sent')
            l.push('Viewed')
            l.push('Expiring')
            l.push('Expired')
            l.push('Not paid')
            l.push('Canceled')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_GIFTS) {
            l.push('Active')
            l.push('Sent')
            l.push('Viewed')
            l.push('Expiring')
            l.push('Expired')
            l.push('Not paid')
            l.push('Canceled')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SUBSCRIPTIONS) {
            l.push('Active')
            l.push('Sent')
            l.push('Viewed')
            l.push('Lapsed')
            l.push('Completed')
            l.push('Not confirmed')
            l.push('Canceled')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ESTIMATES) {
            l.push('Open')
            l.push('Sent')
            l.push('Viewed')
            l.push('Accepted')
            l.push('Deposit paid')
            l.push('Invoiced')
            l.push('Scheduled')
            l.push('Canceled')
            if (PSApplication.role() > User.PERM_ROLE_CLIENT) l.push('Saved')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CONTRACTS) {
            l.push('Signed')
            l.push('Sent')
            l.push('Viewed')
            l.push('Canceled')
            l.push('Unsigned')
            l.push('Expiring')
            l.push('Expired')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RECORDS) {
            l.push('Completed')
            l.push('Sent')
            l.push('Viewed')
            l.push('Canceled')
            l.push('Incomplete')
            l.push('Expiring')
            l.push('Expired')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ITEMS) {
            l.push('All items')
            l.push('Services')
            if (this.owner?.allowPackage) l.push('Packages')
            if (this.owner?.allowSubscription) l.push('Subscriptions')
            if (this.owner?.allowReservation) l.push('Reservations')
            if (this.owner?.allowProduct) l.push('Products')
            if (this.owner?.allowClass) l.push('Classes')
            if (this.owner?.allowGift) l.push('Gift certificates')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_TASKS) {
            l.push('Open')
            l.push('Completed')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_LINKS) {
            l.push('Views')
            l.push('Sessions')
            l.push('Clicks')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_TRIGGERS) {
            l.push('Active')
            l.push('Completed')
            l.push('Cancelled')
            l.push('Paused')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_LOCATIONS) {
            l.push('Active')
            l.push('Past')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SUBSCRIBERS) {
            l.push('All')
            l.push('Paying')
            l.push('Lapsed')
            l.push('Past')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CANCELLATIONS) {
            l.push('All')
            l.push('Late')
            l.push('No show')
            l.push('Courtesy')
            l.push('') // TODO: 'Clients', currently not supported
            l.push('Rescheduling')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY) {
            l.push('Inventory status')
            l.push('Products to restock')
        }
        return l
    }

    get viewOptions(): SelectOption[] {
        return this.viewStrings
            .map((s, i) => ({ value: i.toString(), label: s }))
            .filter((o) => !!o.label)
    }

    get defaultViewOption(): SelectOption {
        return (
            this.viewOptions.find((option) => option.value === this.view?.toString()) || {
                value: '',
                label: 'All',
            }
        )
    }

    get hasDateSegments(): boolean {
        return (
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_EMPLOYEES &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_TRIGGERS &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_PAYOUTS &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_QUICKSTART &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_VERIFICATIONS
        )
    }

    get hasDrilldownImage(): boolean {
        return (
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_DEPOSITS &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_TRIGGERS &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_QUICKSTART &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_VERIFICATIONS &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_LINKS
        )
    }

    get showOnDashboard(): boolean {
        return (
            // TODO: tl;dr - decouple DashboardComponent action buttons (i.e. charge, schedule, and invoice) from this flag
            //
            // The charge, schedule, and invoice action buttons appear on the main dashboard; they should only appear if the
            // respective dashboards are returned when calling RecordFactory fetchDashboards. Additionally, fetchDashboards
            // only returns dashboards in the onLoad if they have the showOnDashboard flag. Desktop currently relies on the
            // onLoad return to show the dashboard actions, while mobile doesn't. Investigate how mobile works around this
            // to show the action buttons, and implement on desktop in a similar manner -- then these exclusions can be added
            // back to this flag (worth noting this flag is currently only used in fetchDashboards):
            //
            // this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_CHARGE &&
            // this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_SCHEDULE &&
            // this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICE &&

            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_BOOK &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_TEXT &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_PAY &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_INBOX &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_CALENDAR &&
            this.type !== DASHBOARD_TYPE.DASHBOARD_TYPE_CONTACTS
        )
    }

    get welcomeIfEmpty(): boolean {
        return (
            this.count === 0 &&
            !this.alwaysDrilldown &&
            this.person === undefined &&
            this.me!.perm > User.PERM_ROLE_CONTRACTOR &&
            !!this.newString &&
            (this.viewCount ?? 0) < 2
        )
    }

    get shouldDisplayNewIcon(): boolean {
        return this.feature !== undefined && this.welcomeIfEmpty
    }

    get forSetup(): boolean {
        return (
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SETUP_LAUNCH ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SETUP_ONBOARDING
        )
    }

    get forAlert(): boolean {
        return (
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_ASAP ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_TEAM ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_RENEW ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_REFER ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_REVIEW ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_READER ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ALERTS_AFFILIATE
        )
    }

    getDateTypeOptions(viewFilter: number): SelectOption[] {
        if (
            (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY && viewFilter === 1) ||
            (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_DOCUMENTS && viewFilter === 1) ||
            (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RECORDS && viewFilter === 5) ||
            (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CONTRACTS && viewFilter === 5)
        ) {
            return [
                { value: 'week', label: 'Next 7 days' },
                { value: 'month', label: 'Next 30 days' },
                { value: 'quarter', label: 'Next 90 days' },
                { value: 'year', label: 'Next 365 days' },
                { value: 'all', label: 'Total (all)' },
            ]
        }
        else if (
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_REVIEWS ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_GOOGLE_LEADS ||
            (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_DOCUMENTS && viewFilter === 2) ||
            (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RECORDS && viewFilter === 6) ||
            (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CONTRACTS && viewFilter === 6)
        ) {
            return [
                { value: 'tw', label: 'This week' },
                { value: 'tm', label: 'This month' },
                { value: 'tq', label: 'This quarter' },
                { value: 'ty', label: 'This year' },
                { value: 'lw', label: 'Last week' },
                { value: 'lm', label: 'Last month' },
                { value: 'lq', label: 'Last quarter' },
                { value: 'ly', label: 'Last year' },
                { value: 'wtd', label: 'Week to date' },
                { value: 'mtd', label: 'Month to date' },
                { value: 'qtd', label: 'Quarter to date' },
                { value: 'ytd', label: 'Year to date' },
                { value: 'pw', label: 'Last 7 days' },
                { value: 'pm', label: 'Last 30 days' },
                { value: 'pq', label: 'Last 90 days' },
                { value: 'py', label: 'Last 365 days' },
                { value: 'today', label: 'Today' },
                { value: 'ld', label: 'Yesterday' },
                { value: 'all', label: 'Total (all)' },
            ]
        }
        else {
            return [
                { value: 'tw', label: 'This week' },
                { value: 'tm', label: 'This month' },
                { value: 'tq', label: 'This quarter' },
                { value: 'ty', label: 'This year' },
                { value: 'nw', label: 'Next week' },
                { value: 'nm', label: 'Next month' },
                { value: 'nq', label: 'Next quarter' },
                { value: 'ny', label: 'Next year' },
                { value: 'lw', label: 'Last week' },
                { value: 'lm', label: 'Last month' },
                { value: 'lq', label: 'Last quarter' },
                { value: 'ly', label: 'Last year' },
                { value: 'wtd', label: 'Week to date' },
                { value: 'mtd', label: 'Month to date' },
                { value: 'qtd', label: 'Quarter to date' },
                { value: 'ytd', label: 'Year to date' },
                { value: 'week', label: 'Next 7 days' },
                { value: 'month', label: 'Next 30 days' },
                { value: 'quarter', label: 'Next 90 days' },
                { value: 'year', label: 'Next 365 days' },
                { value: 'pw', label: 'Last 7 days' },
                { value: 'pm', label: 'Last 30 days' },
                { value: 'pq', label: 'Last 90 days' },
                { value: 'py', label: 'Last 365 days' },
                { value: 'today', label: 'Today' },
                { value: 'nd', label: 'Tomorrow' },
                { value: 'ld', label: 'Yesterday' },
                { value: 'all', label: 'Total (all)' },
            ]
        }
    }

    getDefaultDateTypeOption(viewFilter: number): SelectOption | undefined {
        return this.dateTypeAsString
            ? this.getDateTypeOptions(viewFilter).find(
                  (option) => option.value === this.dateTypeAsString
              )
            : undefined
    }

    get status(): string | undefined {
        if (this._status) return this._status
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PAYOUTS) {
            let date = this.metaData.valueForTimeStampKey('payout_date')
            if (moment().diff(date) > 0) date = Payment.payoutDate(date)
            if (date?.startOf('day').isSame(moment().startOf('day')))
                return `Next payout today at ~${date!.format('h:mm a').toLowerCase()}`
            else if (!date) return 'There is no scheduled payout date'
            return `Next payout ${date.format('MMM D')} at ~${date.format('h:mm a').toLowerCase()}`
        }
        return undefined
    }

    get longStatus(): string | undefined {
        return this._longStatus ?? this.status
    }

    get valueString(): string | undefined {
        let string = this.count!.toString()
        if (
            (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_COMPLETE &&
                this.me?.perm !== User.PERM_ROLE_CONTRACTOR) ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PAYOUTS ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PENDING ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_EXPENSES ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ROLLUP_SALES ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INCOME ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICES ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_BILLINGS ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ESTIMATES ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_DEPOSITS ||
            (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PACKAGES && this.view === 5)
        )
            string = currencyFloat(this.value)
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_EMPLOYEES)
            string = this.value === 1 ? '1 team member' : `${this.value} team members`
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PACKAGES)
            string = this.value === 1 ? '1 session' : `${this.value} sessions`
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_GIFTS) string = `${this.value} balance`
        else if (
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_QUICKSTART ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_VERIFICATIONS ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_REVIEWS
        )
            string = this.status!
        return string
    }

    get countString(): string | undefined {
        if (
            (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_COMPLETE &&
                this.me?.perm !== User.PERM_ROLE_CONTRACTOR) ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PAYOUTS ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PENDING ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_EXPENSES ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ROLLUP_SALES ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INCOME ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICES ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_BILLINGS ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ESTIMATES ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_GIFTS ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PACKAGES ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_DEPOSITS ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_REVIEWS
        )
            return this.count!.toString()
        if (
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_QUICKSTART ||
            this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_VERIFICATIONS
        )
            return this.value !== undefined ? Math.floor(this.value)!.toString() : undefined
        return undefined
    }

    get buttonString(): string | undefined {
        const me = this.me!
        if (me.sp && this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_DEPOSITS)
            return 'View account balance'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_LINKS && (me.solo || me.company))
            return 'Edit your Suite link'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_TRIGGERS && (me.solo || me.company)) {
            const credits = this.metaData.valueForNumberKey('credits')
            return credits === 0
                ? 'Add credits'
                : credits === 1
                  ? '1 credit left'
                  : `${credits} credits left`
        }
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PAYOUTS) {
            const instantBalance = this.metaData.valueForCurrencyKey('instant_balance') ?? 0
            if (instantBalance > 0) return `Cash out ${currencyFloat(instantBalance)}`
        }
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_REVIEWS && this.view === 0) {
            const total = this.metaData.valueForIntKey('total_sent_to_google') ?? 0
            if (total === 1) return '1 client sent to google'
            else if (total > 1) return `${total} clients sent to google`
        }
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICES) return 'Create'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_BOOKINGS) return 'Schedule client'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ORDERS) return 'Create'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ESTIMATES) return 'Create'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PACKAGES) return 'Sell'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_GIFTS) return 'Sell'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SUBSCRIPTIONS) return 'Sell'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CONTRACTS) return 'Send'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RECORDS) return 'Send'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY) return 'Add product'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RESERVATIONS)
            return 'Schedule a reservation'
        return undefined
    }

    get buttonLinkUrl(): string | undefined {
        const me = this.me!
        if (me.sp && this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_DEPOSITS)
            return '/dashboards/deposits/balance'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_LINKS && (me.solo || me.company))
            return '/settings/link'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICES) return '/invoices/new'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_BOOKINGS) return '/bookings/new'
        // if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ORDERS) return '/order/new' // TODO: add back when orders project is complete
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ESTIMATES) return '/estimates/new'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PACKAGES) return '/packages/new'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_GIFTS) return '/gifts/new'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SUBSCRIPTIONS) return '/subscriptions/new'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CONTRACTS) return '/contracts/send'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RECORDS) return '/forms/send'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY) return '/settings/products/new'
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RESERVATIONS) return '/bookings/new'
        return undefined
    }

    get newString(): string | undefined {
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICE) return 'Create invoice'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_BOOKINGS) return 'Schedule client'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ORDERS) return 'Place order'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ESTIMATES) return 'Send estimate'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PACKAGES) return 'Sell packages'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_GIFTS) return 'Sell gift certificates'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SUBSCRIPTIONS)
            return 'Auto-charge members'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CONTRACTS) return 'Send a contract'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RECORDS) return 'Send a form'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CONTACTS) return 'Add/import clients'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CHARGE) return 'Charge a card'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SCHEDULE) return 'Schedule client'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INBOX) return 'Send message'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CLASSES) return 'Schedule classes'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CALENDAR) return 'Sync your calendar'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY)
            return 'Add products for sales'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_LINKS) return 'Get my Suite link'
        return undefined
    }

    get emptyString(): string | undefined {
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICES) return 'Send an invoice'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_BOOKINGS) return 'Schedule a client'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ORDERS) return 'Place an order'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ESTIMATES) return 'Send an estimate'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PACKAGES) return 'Sell a package'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_GIFTS) return 'Sell a gift certificate'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SUBSCRIPTIONS)
            return 'Auto-charge a client'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CONTRACTS) return 'Send a contract'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RECORDS) return 'Send a form'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_EMPLOYEES) return 'Add a team member'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_TRIGGERS)
            return 'Create a smart campaign'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_BILLINGS) return 'Schedule an invoice'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_TASKS) return 'Create a task'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY) return 'Add a product'
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_EXPENSES) return 'Send a payment'
        return undefined
    }

    get welcomeString(): string[] | undefined {
        const l = []
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVOICE) {
            l.push('Create and send professional invoices directly from your phone!')
            l.push('Create an invoice, preview it, and send it to your client.')
            l.push(
                'Your clients can receive an invoice by text and/or email. They can tap the link or reply 1 to pay.'
            )
            l.push('Get notified as soon as your client pays their invoice.')
            l.push('Create an invoice by adding a client and a few details.')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INCOME) {
            l.push(
                'The income dashboard has powerful reports on your income, top clients, best sellers and more.'
            )
            l.push(
                'See a detailed list of your income including credit card payments, cash, check, and more.'
            )
            l.push(
                "All of your client payments are safely and securely stored. It's easy to print and (re)send receipts to clients."
            )
            l.push(
                'Easily process refunds for clients. Your clients are automatically notified and they receive refunds within 7 to 10 days.'
            )
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_RECORDS) {
            l.push(
                'Send and get forms or questionnaires completed by clients over text in minutes.'
            )
            l.push(
                'Deliver a form to your clients by text message or email and get notified as soon as it is completed.'
            )
            l.push(
                'Clients can complete and submit your form from their phone or a desktop computer.'
            )
            l.push(
                'Forms are securely stored so you can track sent and signed forms all in one place.'
            )
            l.push(
                "Add a new form in the app in minutes!\n\nNeed help? Use our default forms or we'll import yours at support@pocketsuite.io."
            )
            l.push('Customize your forms with different types of input and question fields.')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CONTRACTS) {
            l.push(
                'Create, send and have contracts signed by clients over text message within minutes.'
            )
            l.push(
                'Deliver contracts via text message or email and get notified as soon as a client signs.'
            )
            l.push('Clients can view and sign contracts from their phone in minutes.')
            l.push(
                'Your contracts are securely stored so you can track sent and signed contracts all in one place.'
            )
            l.push(
                "Setup a new contract in just a couple steps.\n\nGot lots of contracts? Send them to support@pocketsuite.io and we'll import them for you!"
            )
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CONTACTS) {
            l.push('Easily store and manage all of your contacts in a single location!')
            l.push(
                'View a client’s profile for a detailed look at their personal information, history, messages, notes, and custom fields.'
            )
            l.push(
                'All of your client information organized in one place. You will always have easy access to every client bill, appointment, message, note, and document.'
            )
            l.push(
                'Add client(s) to the app in seconds.\n\nNeed help? We’ll import your client list at support@pocketsuite.io.'
            )
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_PACKAGES) {
            l.push('Offer a bundle of sessions, get paid upfront, and track usage.')
            l.push(
                'You and your clients can track all of your active, expiring, and expired packages in one place.'
            )
            l.push(
                'Keep track of all sessions used and the remaining session balance on each package.'
            )
            l.push(
                'Clients can purchase packages directly from their phone. Receipts are automatically sent.'
            )
            l.push(
                'When creating a new package you can specify the number of sessions, price, expiration date, and eligible services.'
            )
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_GIFTS) {
            l.push(
                'If your clients ❤️ you, they’ll refer friends. If they REALLY ❤️ you, they’ll send their friends your gift certificate.'
            )
            l.push('Personalize and sell your gift certificate to clients.')
            l.push(
                'Clients can pay for gift certificates from their phone or buy them from your booking site.'
            )
            l.push('Clients can transfer a gift certificate and add a special message.')
            l.push('Track the remaining balance and usage of all of your gift certificates.')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY) {
            l.push('All of your products and prices stored and displayed online.')
            l.push(
                'Automatically track inventory so you no longer need to physically count your products.'
            )
            l.push('Add your product, price, description, and an image.')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CLASSES) {
            l.push(
                'You can offer a variety of classes, ranging from drop in classes, series, single lesson, or multiple lessons.'
            )
            l.push(
                'Schedule clients for single or multiple classes. Clients can prepay or confirm with credit card.'
            )
            l.push('Book clients, send messages, and view your class roster.')
            l.push(
                'Your roster shows clients who have registered for your class; those on the waitlist; and how much space you have left.'
            )
            l.push(
                'Quickly add class details such as price, class size, and location.\n\nPro Tip: Have clients pay an upfront deposit to protect yourself against cancellations.'
            )
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_ESTIMATES) {
            l.push('Estimates are a powerful way to send clients quotes.')
            l.push('Create and send professional estimates directly from your phone!')
            l.push(
                'Deliver estimates via text message or email and allow clients to view them with one tap on their mobile phone.'
            )
            l.push('Easily convert with one tap an estimate into an invoice or appointment.')
            l.push(
                'To get started simply add a few pieces of information, preview it, and then send it to your client. '
            )
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SUBSCRIPTIONS) {
            l.push(
                'Subscriptions are a great way to collect repeat payments from clients. They are great for memberships, auto-renewing packages, and installment plans.'
            )
            l.push(
                'Track the status of all of your subscriptions in one place. Automatic followup with delinquent clients.'
            )
            l.push('Keep track of your clients subscription payments and next payment dates.')
            l.push(
                'Clients can confirm their subscription plan and add a card for payment from their phone in minutes.'
            )
            l.push(
                'Create a subscription and add details such as price, frequency, and number of payment periods.'
            )
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CHARGE) {
            l.push('Easily charge client credit cards.')
            l.push('Review and process payment!')
            l.push(
                'To charge card, you can swipe, tap, scan, insert card to read chip, or enter card number directly.'
            )
            l.push('Choose to send a receipt by text or email.')
            l.push('Your clients get professional receipts.')
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_SCHEDULE) {
            l.push('With PocketSuite you can easily schedule appointments with your clients!')
            l.push('Enter appointment details and send.')
            l.push('Setup repeat client appointments to lock in future income.')
            l.push(
                'You can auto confirm appointments or require clients to confirm with credit card.'
            )
            l.push(
                'Your clients will receive the appointment notification by text and/or email. They can tap 1 to confirm the appointment.'
            )
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INBOX) {
            l.push(
                'With PocketSuite Messenger you can send and receive client messages from your dedicated business number.'
            )
            l.push(
                'Send your clients text messages and get responses in minutes. Faster bookings, faster payments, and faster communication!'
            )
            l.push("Have a question? Text PocketSuite, we're available 7 days a week!")
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_LINKS) {
            l.push(
                'Suite link is a one stop shop for all of your social media profiles, websites, and any other links.'
            )
            l.push('Customize the color and rearrange the order of the links.')
            l.push(
                'To add a link, simply add the URL and title you would like listed on the button.'
            )
            l.push(
                'See how many people have viewed it, what links they clicked on, and where they found you.'
            )
            l.push(
                'Easily send a Suite link by tapping on the "www" icon at the bottom of the messages screen.'
            )
        }
        else if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_CALENDAR) {
            l.push(
                "PocketSuite's calendar allows you to easily organize and schedule all of your appointments."
            )
            l.push(
                "Sync your personal calendar with PocketSuite so you don't double book and your availability is always up to date."
            )
            l.push('Schedule clients or block off time directly from your calendar.')
            l.push('View your availability and easily schedule client appointments.')
            l.push(
                'Set your hours, booking preferences, and appointment confirmation requirements.'
            )
        }
        return l
    }

    get moreButtonTitle(): string | undefined {
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY) return 'Product details'
        return undefined
    }

    get moreButtonMessage(): string | undefined {
        if (this.type === DASHBOARD_TYPE.DASHBOARD_TYPE_INVENTORY)
            return 'View product history to see pricing changes, quantity adjustments, etc. You can also edit the unit quantity as needed based on recent orders and purchases'
        return undefined
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.feature) this.feature = BaseRecord.fromSerializer(data.feature, Feature)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        features: ['feature'],
    }

    static dataType(): DataType {
        return 'dashboards'
    }

    get feature(): Feature | undefined {
        return this.getForeignRecord('feature', Feature)
    }
    set feature(feature: Feature | undefined) {
        this.setForeignRecord('feature', feature)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get subaccount(): Subaccount | undefined {
        return this.getForeignRecord('subaccount', Subaccount)
    }
    set subaccount(subaccount: Subaccount | undefined) {
        this.setForeignRecord('subaccount', subaccount)
    }
}
